import type { ComponentProps, ReactElement } from 'react'

import { i18n } from '@lingui/core'
import { isNil } from 'ramda'
import { useFormContext } from 'react-hook-form'

import { FormHelperText } from '@twisto/components/atoms/form-helper-text'
import { Input } from '@twisto/components/atoms/input'
import { InputLabel } from '@twisto/components/atoms/input-label'
import { NativeSelect } from '@twisto/components/atoms/native-select'
import {
  getCallingCodeConfig,
  normalizePhoneNumber,
  validator,
} from '@twisto/form'
import type { CallingCode } from '@twisto/form'

import * as styles from './phone-number-field.styles'

type Props = {
  name: string
  label?: string | ReactElement
  callingCodes: CallingCode[] | string[]
  isRequired?: boolean
  helperText?: string | undefined | ReactElement
} & Pick<
  ComponentProps<typeof Input>,
  'error' | 'autoFocus' | 'disabled' | 'autoComplete' | 'readOnly'
>

export type PhoneNumberFieldFormValues = {
  callingCode: CallingCode | string
  nationalNumber: string
}

export const PhoneNumberField = ({
  name,
  label,
  callingCodes,
  helperText,
  error: errorProp = false,
  isRequired = false,
  disabled,
  ...others
}: Props) => {
  // results in name:{callingCode:'',nationalNumber:''}
  const callingCodeName = `${name}.callingCode`
  const nationalNumberName = `${name}.nationalNumber`

  const { formState, watch, clearErrors, register } = useFormContext()
  const fieldErrors = formState.errors

  const hasError =
    errorProp ||
    Boolean(fieldErrors[name]) ||
    Boolean(fieldErrors[callingCodeName]) ||
    Boolean(fieldErrors[nationalNumberName])

  const currentCallingCode = watch(callingCodeName) ?? callingCodes[0]
  const currentCallingCodeConfig = getCallingCodeConfig(currentCallingCode)

  const validate = (value: string) => {
    const isRequiredError = validator.formValidators.isRequired(value)
    if (isRequired && !isNil(isRequiredError)) {
      return isRequiredError
    }

    if (!isNil(currentCallingCodeConfig)) {
      return validator.formValidators.isValidPhoneNumber(
        value,
        currentCallingCodeConfig.format
      )
    }
  }

  const a11y = {
    /*i18n*/
    'arial-label': i18n._('form.fields.phoneNumber.ariaLabel'),
  }

  const callingCodeEditEnabled = callingCodes.length > 1

  return (
    <div css={styles.direction}>
      {!isNil(label) && <InputLabel>{label}</InputLabel>}
      <div css={styles.root}>
        {callingCodeEditEnabled && (
          <NativeSelect
            css={styles.select}
            disabled={disabled}
            error={hasError}
            name={callingCodeName}
          >
            {callingCodes.map((value) => (
              <option key={value} value={value}>
                {value}
              </option>
            ))}
          </NativeSelect>
        )}

        {!callingCodeEditEnabled && (
          <input
            {...register(callingCodeName)}
            role="none"
            type="hidden"
            value={currentCallingCode}
          />
        )}

        <Input
          {...a11y}
          disabled={disabled}
          error={hasError}
          formattedOptions={currentCallingCodeConfig?.format}
          name={nationalNumberName}
          placeholder={currentCallingCodeConfig?.placeholder}
          rules={{ validate }}
          startAdornment={
            callingCodeEditEnabled ? undefined : currentCallingCode
          }
          styles={
            callingCodeEditEnabled
              ? {
                  startAdornment: styles.startAdornment,
                }
              : {
                  root: styles.input,
                }
          }
          type="tel"
          onChange={(event) => {
            clearErrors(name)
            // removes spaces from "000 000 000"
            // eslint-disable-next-line no-param-reassign
            event.target.value = normalizePhoneNumber(event.target.value)
          }}
          {...others}
        />
      </div>
      <FormHelperText
        disabled={disabled}
        errors={fieldErrors}
        isError={hasError}
        name={nationalNumberName}
      >
        {helperText}
      </FormHelperText>
      <FormHelperText
        disabled={disabled}
        errors={fieldErrors}
        isError={hasError}
        name={name}
      >
        {helperText}
      </FormHelperText>
    </div>
  )
}
